import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Pin, PinOff, Eye, EyeOff, Search, Archive, ArchiveRestore, GripVertical, Copy } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects", {
        params: {
          limit: 100,
          include_unpublished: true,
          include_archived: true,
          sort: "manual",
          search: search || undefined,
        },
      });
      const items = showArchived ? data.items : data.items.filter((p) => !p.is_archived);
      setProjects(items);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const togglePublish = async (p) => {
    try {
      await api.patch(`/projects/${p.id}`, { is_published: !p.is_published });
      toast.success(p.is_published ? "Unpublished" : "Published");
      load();
    } catch { toast.error("Failed to update"); }
  };

  const togglePin = async (p) => {
    try {
      await api.patch(`/projects/${p.id}`, { is_pinned: !p.is_pinned });
      toast.success(p.is_pinned ? "Unpinned" : "Pinned");
      load();
    } catch { toast.error("Failed to update"); }
  };

  const toggleArchive = async (p) => {
    try {
      await api.patch(`/projects/${p.id}`, { is_archived: !p.is_archived });
      toast.success(p.is_archived ? "Unarchived" : "Archived");
      load();
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const duplicate = async (p) => {
    try {
      const { data } = await api.post(`/admin/projects/${p.id}/duplicate`);
      toast.success(`Duplicated as "${data.title}"`);
      load();
    } catch { toast.error("Duplicate failed"); }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = projects.findIndex((p) => p.id === active.id);
    const newIndex = projects.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(projects, oldIndex, newIndex);
    setProjects(next);
    setReordering(true);
    try {
      await api.post("/admin/projects/reorder", { ordered_ids: next.map((p) => p.id) });
      toast.success("Order saved");
    } catch {
      toast.error("Failed to save order");
      load();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div data-testid="admin-projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-slate-950">Projects</h1>
          <p className="text-slate-500 text-sm mt-1">{projects.length} projects · drag the handle to reorder</p>
        </div>
        <Link
          to="/admin/projects/new"
          data-testid="new-project-btn"
          className="inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 text-white px-4 py-2.5 rounded-md text-sm font-medium w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" /> New Project
        </Link>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search projects…"
            data-testid="admin-projects-search"
            className="w-full pl-11 pr-4 py-2.5 rounded-md border border-slate-200 text-sm bg-white"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="toggle-show-archived"
          />
          Include archived
        </label>
        {reordering && <span className="text-xs text-slate-400">Saving order…</span>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-10 text-slate-400">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="text-center py-10 text-slate-400">No projects.</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={projects.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              <ul className="divide-y divide-slate-100" data-testid="admin-projects-list">
                {projects.map((p) => (
                  <SortableRow
                    key={p.id}
                    p={p}
                    onPin={togglePin}
                    onPublish={togglePublish}
                    onArchive={toggleArchive}
                    onDuplicate={duplicate}
                    onDelete={remove}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}

function SortableRow({ p, onPin, onPublish, onArchive, onDuplicate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 20 : "auto",
    background: isDragging ? "#F8FAFC" : "white",
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`admin-project-row-${p.slug}`}
      className={`flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 px-3 py-3 ${p.is_archived ? "opacity-60" : ""}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`drag-handle-${p.slug}`}
        className="p-1.5 text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <img src={fileUrl(p.cover_image)} alt="" className="w-12 h-12 object-cover rounded-md flex-shrink-0" />

      <div className="flex-1 min-w-[40%] sm:min-w-0">
        <div className="font-medium text-slate-950 text-sm line-clamp-1">{p.title}</div>
        <div className="text-xs text-slate-500 line-clamp-1">{p.location} · <span className="font-mono break-all">{p.slug}</span></div>
        <div className="flex flex-wrap gap-1 mt-1">
          {!p.is_published && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Draft</span>}
          {p.is_pinned && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Pinned</span>}
          {p.is_archived && <span className="text-[10px] px-1.5 py-0.5 bg-slate-200 text-slate-700 rounded">Archived</span>}
          {p.is_video_project && <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">Video</span>}
        </div>
      </div>

      <div className="hidden lg:block text-xs text-slate-500 px-2 flex-shrink-0">
        {p.view_count} views · {p.like_count} likes
      </div>

      <div className="inline-flex flex-wrap gap-0.5 flex-shrink-0 justify-end">
        <button onClick={() => onPin(p)} title="Pin/Unpin" data-testid={`pin-${p.slug}`} className="p-2 hover:bg-slate-100 rounded">
          {p.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
        </button>
        <button onClick={() => onPublish(p)} title="Publish/Unpublish" data-testid={`publish-${p.slug}`} className="p-2 hover:bg-slate-100 rounded">
          {p.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button onClick={() => onArchive(p)} title={p.is_archived ? "Unarchive" : "Archive"} data-testid={`archive-${p.slug}`} className="p-2 hover:bg-slate-100 rounded">
          {p.is_archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
        </button>
        <button onClick={() => onDuplicate(p)} title="Duplicate" data-testid={`duplicate-${p.slug}`} className="p-2 hover:bg-slate-100 rounded">
          <Copy className="w-4 h-4" />
        </button>
        <Link to={`/admin/projects/edit/${p.id}`} data-testid={`edit-${p.slug}`} className="p-2 hover:bg-slate-100 rounded">
          <Pencil className="w-4 h-4" />
        </Link>
        <button onClick={() => onDelete(p)} data-testid={`delete-${p.slug}`} className="p-2 hover:bg-rose-50 rounded text-rose-600">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}
